class AddedToCartPopup extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.panel = this.querySelector('[data-popup-panel]');
    this.image = this.querySelector('[data-popup-image]');
    this.titleEl = this.querySelector('[data-popup-title]');
    this.variantEl = this.querySelector('[data-popup-variant]');
    this.recommendations = this.querySelector('[data-popup-recommendations]');
    this.footer = this.querySelector('[data-popup-footer]');

    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-popup-close]')) {
        e.preventDefault();
        this.close();
      }
    });
  }

  open() {
    this.classList.remove('invisible', 'opacity-0', 'pointer-events-none');
    this.classList.add('opacity-100', 'pointer-events-auto');
    this.panel?.classList.remove('-translate-y-4');
    this.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.classList.add('opacity-0', 'pointer-events-none');
    this.classList.remove('opacity-100', 'pointer-events-auto');
    this.panel?.classList.add('-translate-y-4');
    this.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    // Hide from the a11y tree + remove from hit-testing once the fade finishes.
    setTimeout(() => {
      if (this.getAttribute('aria-hidden') === 'true') this.classList.add('invisible');
    }, 300);
  }

  // Populate the success header from a /cart/add.js line item.
  populate(item) {
    if (!item) return;

    if (this.image) {
      const src = item.image || (item.featured_image && item.featured_image.url);
      if (src) {
        this.image.src = src;
        this.image.alt = (item.featured_image && item.featured_image.alt) || item.product_title || '';
      }
    }

    if (this.titleEl) this.titleEl.textContent = item.product_title || item.title || '';

    if (this.variantEl) {
      let variant = '';
      if (Array.isArray(item.options_with_values) && item.options_with_values.length) {
        variant = item.options_with_values
          .map((o) => o.value)
          .filter((v) => v && v !== 'Default Title')
          .join(', ');
      } else if (item.variant_title && item.variant_title !== 'Default Title') {
        variant = item.variant_title;
      }
      this.variantEl.textContent = variant;
      this.variantEl.classList.toggle('hidden', !variant);
    }
  }

  // Reveal the carousel + bottom button block only when there are upsells. Mobile panel is
  // always full-height (buttons anchor to the bottom); desktop hugs content.
  setUpsellVisible(visible) {
    this.recommendations?.classList.toggle('hidden', !visible);
    if (this.footer) {
      this.footer.classList.toggle('hidden', !visible);
      this.footer.classList.toggle('flex', visible);
    }
  }

  async loadRecommendations(productId) {
    this.setUpsellVisible(false);
    if (!this.recommendations || this.dataset.showRecommendations === 'false' || !productId) return;
    const base = this.dataset.recommendationsUrl;
    if (!base) return;

    const section = this.dataset.recommendationsSection || 'added-to-cart-recommendations';
    const intent = this.dataset.intent || 'related';
    const limit = this.dataset.limit || 4;
    const url = `${base}?section_id=${section}&product_id=${productId}&intent=${intent}&limit=${limit}`;

    try {
      const response = await fetch(url);
      if (!response.ok) {
        this.recommendations.innerHTML = '';
        return;
      }
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-product-recommendations]');
      this.recommendations.innerHTML = fresh ? fresh.innerHTML : '';
      this.setUpsellVisible(this.recommendations.querySelector('.product-card') != null);
    } catch (err) {
      console.error('Popup recommendations failed', err);
      this.recommendations.innerHTML = '';
    }
  }
}

if (!customElements.get('added-to-cart-popup')) {
  customElements.define('added-to-cart-popup', AddedToCartPopup);
}

// The popup owns the add-to-cart confirmation (the cart drawer only refreshes its badge).
// Quick-add-from-card clicks are tagged `quickAdd` and skip the popup — they get their own
// inline button success state instead (see the submit handler below).
document.addEventListener('cart:added', (event) => {
  const item = event.detail || {};
  if (item.quickAdd) return;
  const popup = document.querySelector('added-to-cart-popup');
  if (!popup || typeof popup.open !== 'function') return;
  popup.populate(item);
  popup.open();
  popup.loadRecommendations(item.product_id);
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const popup = document.querySelector('added-to-cart-popup');
  if (popup && popup.getAttribute('aria-hidden') === 'false') popup.close();
});

// Product-card quick-add buttons (recommendations + grids) add via AJAX and show a green
// checkmark on the button itself — they never open the popup (see the quickAdd flag above).
function showQuickAddSuccess(button) {
  if (!button) return;
  const icon = button.querySelector('[data-quick-add-icon]');
  const template = button.parentElement?.querySelector('[data-quick-add-success-icon]');
  if (!icon || !template) return;
  if (button._quickAddRevertTimer) clearTimeout(button._quickAddRevertTimer);

  const originalIcon = button._quickAddOriginalIcon ?? (button._quickAddOriginalIcon = icon.innerHTML);
  icon.innerHTML = template.innerHTML;
  button.style.backgroundColor = '#22c55e';
  button.setAttribute('aria-label', button.dataset.labelAdded || button.getAttribute('aria-label'));

  button._quickAddRevertTimer = setTimeout(() => {
    icon.innerHTML = originalIcon;
    button.style.backgroundColor = '';
    button.setAttribute('aria-label', button.dataset.labelDefault || button.getAttribute('aria-label'));
  }, 3000);
}

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('form.product-card__quick-add');
  if (!form) return;
  event.preventDefault();
  const button = form.querySelector('button[type="submit"]');
  button?.setAttribute('disabled', 'true');
  try {
    const formData = new FormData(form);
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(await response.text());
    const data = await response.json();
    document.dispatchEvent(new CustomEvent('cart:added', { detail: { ...data, quickAdd: true } }));
    showQuickAddSuccess(button);
  } catch (err) {
    console.error('Quick add failed', err);
    document.dispatchEvent(new CustomEvent('cart:error', { detail: err.message }));
  } finally {
    button?.removeAttribute('disabled');
  }
});
